import moment from "moment";
import {
  osName,
  isTablet,
  isMobile,
  isDesktop,
  browserName,
  mobileVendor,
} from "react-device-detect";
import { Dispatch } from "react";
import { AxiosResponse } from "axios";

import { showAlert } from "./alert";
import { getAccessToken } from "./session";
import { setCMSLinks } from "../../redux/global.slice";
import { EndPoint, Env, IMAGES, Module } from "../constants";
import { apiSuccessCode, $axios } from "../constants/axios.instance";

type DeviceInfo = {
  osName: string;
  browser: string;
  deviceType: string;
  deviceManufacturer: string;
};

type PolicyItem = {
  _id: string;
  type: "privacy_policy" | "terms_of_use" | "your_privacy_choices";
  content: string;
};

type PolicyData = {
  privacyPolicy: string;
  termsOfUse: string;
  yourPrivacyChoices: string;
};

type NumberInputEvent = Pick<KeyboardEvent, "key" | "preventDefault">;

/**
 * Blocks non-numeric keyboard input while still allowing common editing keys.
 */
export const handleNumberInput = (event: NumberInputEvent) => {
  const isDigit = /^\d$/.test(event.key);

  const allowedControlKeys: ReadonlyArray<NumberInputEvent["key"]> = [
    "Backspace",
    "ArrowLeft",
    "ArrowRight",
    "Delete",
    "Tab",
  ];

  if (!isDigit && !allowedControlKeys.includes(event.key)) {
    event.preventDefault();
  }
};

/**
 * Returns true when the supplied status code matches any of the known axios success codes.
 */
export const isAPISuccessful = (code: number): boolean => {
  return [
    apiSuccessCode.GET_SUCCESS,
    apiSuccessCode.POST_SUCCESS,
    apiSuccessCode.PUT_SUCCESS,
  ].includes(code);
};

/**
 * Formats a given value into `MM/DD/YYYY`. Returns an empty string for invalid dates.
 */
export const FormatDate = (date: any): string => {
  return moment(date).format("MM/DD/YYYY");
};

/**
 * Converts a unix timestamp in milliseconds to an `MM/DD/YYYY` string.
 */
export const convertTimestampToDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const month = ("0" + (date.getMonth() + 1)).slice(-2); // Months are 0-indexed in JS
  const day = ("0" + date.getDate()).slice(-2);
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Appends the base media upload URL to the provided image path.
 * @param {string} imagePath - The file path returned from the upload API.
 * @param {boolean} [isIPFS] - Optional flag to indicate if the image is stored on IPFS.
 * @returns {string} - The full URL for accessing the uploaded file.
 */
export const appendImageUrl = (imagePath: string, isIPFS?: Boolean): string => {
  if (
    imagePath?.startsWith("databis") ||
    imagePath?.startsWith("databis/media")
  )
    return `${Env.MEDIA_URL}/${imagePath}`;
  else if (
    !(
      imagePath?.startsWith("databis") || imagePath?.startsWith("databis/media")
    ) &&
    isIPFS
  )
    return `${Env.IPFS_GATEWAY_URL}${imagePath}`;
  else return imagePath;
};

/**
 * Appends the IPFS URL to the provided path.
 * @param {string} path - The path returned from API.
 * @returns {string} - The full URL for accessing the IPFS file.
 */
export const appendIPFSUrl = (path: string): string =>
  `${Env.IPFS_GATEWAY_URL}${path}`;

/**
 * Checks the internet connection status and displays an alert if offline.
 *
 * @param {Function} showAlert - A callback function to display an alert.
 *        - `type` (number): Alert type (e.g., 2 for error).
 *        - `message` (string): The alert message to be displayed.
 * @returns {boolean} - Returns `true` if online, `false` if offline.
 */
export const checkInternetAndShowAlert = (
  showGlobalAlert: (type: number, message: string) => void,
): boolean => {
  // Check if the browser is offline
  if (!navigator.onLine) {
    // Trigger an alert to notify the user about connectivity issues
    showGlobalAlert(
      2,
      "We're experiencing technical difficulties. Please try again later.",
    );

    return false; // Return false to indicate no internet connection
  }

  return true; // Return true if internet is available
};

/**
 * Rounds a numeric value (string or number) to two decimal places.
 *
 * @param {string | number} value - The value to round. Can be a number or a numeric string.
 * @returns {number} The number rounded to two decimal places. Returns NaN for invalid input.
 */
export const roundToTwo = (value: string | number) => {
  // Convert the input to a number
  const num = Number(value);

  // If conversion fails, return NaN
  if (isNaN(num)) return NaN;

  // Round to two decimal places
  return Number(num.toFixed(2));
};

/**
 * Rounds a numeric value (string or number) to three decimal places.
 *
 * @param {string | number} value - The value to round. Can be a number or a numeric string.
 * @returns {number} The number rounded to three decimal places. Returns NaN for invalid input.
 */
export const roundToThree = (value: string | number) => {
  const num = Number(value);

  if (isNaN(num)) return NaN;

  // Round to 3 decimal places
  const rounded = num.toFixed(3);

  // Remove trailing zeros and optional decimal point
  return rounded.replace(/\.?0+$/, "");
};

/** Truncates a number to a fixed number of decimal places without rounding. */
export const truncateToDecimals = (
  num: number,
  decimalPlaces: number = 2,
): number => {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.floor((num + Number.EPSILON) * multiplier) / multiplier;
};

/**
 * Formats a rating value by truncating it to two decimal places without rounding.
 *
 * @param {string | number | undefined | null} rating - The rating value to format.
 * @returns {string} The formatted rating (e.g. "4.56"), or "-" if not valid/provided.
 */
export const formatRating = (rating?: number | string | null): string => {
  if (rating === undefined || rating === null || rating === "") {
    return "-";
  }
  const num = Number(rating);
  if (isNaN(num)) {
    return "-";
  }
  return truncateToDecimals(num, 2).toFixed(2);
};

export const getInitials = (name: string) => {
  if (!name) {
    return "";
  }

  /**
   * `word[0]` is safe after the filter since empty segments are removed.
   */
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2); // limit to 2 initials
};

/**
 * Masks a blockchain address, keeping the first two and last six characters visible.
 */
export const formatAddress = (address: string): string => {
  if (!address) {
    return "";
  }

  if (address.length <= 8) {
    return address.toUpperCase();
  }

  const prefix = address.slice(0, 2).toUpperCase(); // Get first 2 characters
  const suffix = address.slice(-6).toUpperCase(); // Get last 6 characters
  return `${prefix}****${suffix}`;
};

/**
 * Collects a minimal snapshot of the client's device/browser to attach to support tickets.
 */
export const getUserDeviceInfo = (): DeviceInfo => {
  let deviceType: DeviceInfo["deviceType"] = "";

  if (isMobile) {
    deviceType = "mobile_device";
  } else if (isTablet) {
    deviceType = "tablet";
  } else if (isDesktop) {
    deviceType = "desktop";
  } else deviceType = "device_type_other";

  const deviceManufacturer =
    mobileVendor !== "none" ? mobileVendor : "device_manufacturer_other";

  return {
    deviceType,
    deviceManufacturer,
    browser: browserName,
    osName: osName || "os_other",
  };
};

/**
 * Fetches CMS footer links and stores them in the global slice.
 * The thunk exits early when there is no internet connection.
 */
export const getCmsLinks = () => (dispatch: Dispatch<any>) => {
  if (!checkInternetAndShowAlert(showAlert)) return;

  $axios
    .get(`${Module.consumer}${EndPoint.CMS_LINKS}`)
    .then((response: AxiosResponse<any>) => {
      const { data } = response;

      if (isAPISuccessful(data.statusCode)) {
        const cmsLinkListing: PolicyItem[] = data?.data || [];

        const desiredCMSListing: PolicyData = cmsLinkListing.reduce<PolicyData>(
          (acc, item) => {
            switch (item.type) {
              case "privacy_policy":
                acc.privacyPolicy = item.content;
                break;
              case "terms_of_use":
                acc.termsOfUse = item.content;
                break;
              case "your_privacy_choices":
                acc.yourPrivacyChoices = item.content;
                break;
              default:
                break;
            }
            return acc;
          },
          {
            privacyPolicy: "",
            termsOfUse: "",
            yourPrivacyChoices: "",
          },
        );
        dispatch(setCMSLinks(desiredCMSListing));
      }
    })
    .catch((error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to fetch CMS links.";
      showAlert(2, errorMessage);
    });
};

/**
 * Convenience flag set when the authentication token is stored in localStorage.
 */
export const isUserLoggedIn = getAccessToken();
/**
 * Returns the correct product type icon based on the provided type.
 */
export const getProductTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "flower":
      return IMAGES.ProductTypeIcons_Flower;

    case "pre-roll":
      return IMAGES.ProductTypeIcons_Pre_Roll;

    case "vaporizer":
      return IMAGES.ProductTypeIcons_Vaporizer;

    case "concentrate":
      return IMAGES.ProductTypeIcons_Concentrate;

    case "edible":
      return IMAGES.ProductTypeIcons_Edible;

    case "topical":
      return IMAGES.ProductTypeIcons_Topical;

    case "tincture":
      return IMAGES.ProductTypeIcons_Tincture;

    case "specialty":
      return IMAGES.ProductTypeIcons_Specialty;

    default:
      return IMAGES.ProductTypeIcons_Other;
  }
};

/**
 * Checks if the provided ID matches the MongoDB ObjectId pattern.
 *
 * @param {string} id - The ID to be validated against the MongoDB ObjectId pattern.
 * @returns {boolean} `true` if the ID matches the MongoDB ObjectId pattern, otherwise `false`.
 */
export const isValidMongoId = (id: string) => {
  const mongoIdRegex = /^[a-f\d]{24}$/i;
  return mongoIdRegex.test(id);
};
